import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ShieldCheck,
  Upload,
  CheckCircle2,
  Clock,
  XCircle,
  FileText,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ProviderVerification — Tier-2 fraud-protection module.
 *
 * Members who want to accept PAID bookings must submit an ID document for
 * admin approval. Founders are exempt and see a green banner explaining
 * that no verification is needed.
 *
 * Mounted at /verification and embedded inside ProfileSettings.
 */
export default function ProviderVerification() {
  const { user, token, loading: authLoading } = useAuth();
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const isExempt = user?.is_admin || user?.membership_tier === 'founder';

  useEffect(() => {
    if (authLoading || !user) return;
    fetchStatus();
  }, [authLoading, user]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/escrow/me/verification`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVerification(res.data.verification);
    } catch {
      // 401 here = not logged in; left to parent.
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File must be under 8 MB');
      return;
    }
    const form = new FormData();
    form.append('document', file);
    form.append('document_type', 'id');
    setUploading(true);
    try {
      const res = await axios.post(`${API_URL}/api/escrow/me/verification`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setVerification(res.data.verification);
      toast.success('Submitted — an admin will review shortly');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center p-8 text-slate-400">
        <Loader2 className="animate-spin mr-2" /> Loading verification status…
      </div>
    );
  }

  if (isExempt) {
    return (
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="text-emerald-600 flex-shrink-0" size={22} />
          <div>
            <h3 className="font-semibold text-emerald-900">Verification not required</h3>
            <p className="text-sm text-emerald-800 mt-1">
              {user.is_admin
                ? 'Admin accounts are auto-verified.'
                : 'Founder members are trusted by default — paid bookings are unlocked for you immediately.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const status = verification?.status || 'none';
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="provider-verification">
      <div className="flex items-start gap-3 mb-4">
        <ShieldCheck className="text-indigo-600 flex-shrink-0" size={24} />
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Provider verification</h3>
          <p className="text-sm text-slate-500">
            Required before customers can pay you for a service. Upload a clear photo of a
            government-issued ID (driver&apos;s licence, passport, or national ID). We review within 24 hours.
          </p>
        </div>
      </div>

      {/* Status badge */}
      {status === 'approved' && (
        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-sm" data-testid="verification-status-approved">
          <CheckCircle2 size={16} /> Approved — paid bookings unlocked.
        </div>
      )}
      {status === 'pending' && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm" data-testid="verification-status-pending">
          <Clock size={16} /> In review. You&apos;ll get an email once approved.
        </div>
      )}
      {status === 'rejected' && (
        <div className="px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-rose-800 text-sm" data-testid="verification-status-rejected">
          <div className="flex items-center gap-2 font-medium">
            <XCircle size={16} /> Rejected
          </div>
          {verification.notes && (
            <p className="text-xs text-rose-700 mt-1">Reason: {verification.notes}</p>
          )}
          <p className="text-xs mt-1">Please re-submit a clearer image.</p>
        </div>
      )}
      {status === 'none' && (
        <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-700 text-sm">
          You haven&apos;t submitted yet.
        </div>
      )}

      {/* Upload control — hidden if already approved */}
      {status !== 'approved' && (
        <div className="mt-5">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            onChange={handleFile}
            className="hidden"
            id="verification-file"
            data-testid="verification-file-input"
          />
          <label
            htmlFor="verification-file"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer text-sm font-medium ${
              uploading
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
            data-testid="verification-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="animate-spin" size={16} /> Uploading…
              </>
            ) : (
              <>
                <Upload size={16} />
                {status === 'rejected' ? 're-submit ID' : status === 'pending' ? 're-upload (replaces current)' : 'Upload ID'}
              </>
            )}
          </label>
          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
            <FileText size={12} /> JPG, PNG, WebP or PDF · max 8 MB · stored privately, viewable only by admins.
          </p>
        </div>
      )}
    </div>
  );
}
